/**
 * «Комментарии» клиента во вкладке «Новый год» (29.10.2026; до 04.11 — «История»): лента заметок к клиенту (телефону) — кто и когда
 * написал, новые сверху. 04.11 (владелец: «ни комментариев нельзя оставить») — открыта сразу в раскрытой карточке, поле «Добавить»
 * сверху. Добавить может любой из офиса, удалить — автор или ЕО (сервер отдаёт can_delete и проверяет сам). История игр — «Инфо».
 */
import { useEffect, useRef, useState } from "react";
import { AutoGrowTextarea } from "../../components/shared";
import { fmtStamp } from "./nyModel";
import { errText, useNyNotes } from "./useNy";
import ny from "./dcNy.module.css";

const MAX = 2000;

/** ✕ с подтверждением вторым нажатием; двойной тап — не подтверждение. armed — надпись второго нажатия («Удалить?», «Убрать?»). */
export function NyDel({ onDelete, label = "Удалить", idle = "✕", armed = "Удалить?" }: { onDelete: () => Promise<void>; label?: string; idle?: string; armed?: string }) {
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  const armedAt = useRef(0);
  const again = `Ещё раз — ${armed.replace(/\?$/, "").toLowerCase()}`;
  useEffect(() => { if (!arm) return; const t = window.setTimeout(() => setArm(false), 3000); return () => window.clearTimeout(t); }, [arm]);
  const click = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    if (!arm) { armedAt.current = Date.now(); setArm(true); return; }
    if (Date.now() - armedAt.current < 400) return;
    setBusy(true);
    try { await onDelete(); } finally { setBusy(false); setArm(false); }
  };
  return (
    <button type="button" className={`${ny.del} ${arm ? ny.delArm : ""}`} onClick={(e) => { void click(e); }} disabled={busy}
      title={arm ? again : label} aria-label={arm ? again : label}>{busy ? "…" : arm ? armed : idle}</button>
  );
}

export function NyNotes({ cardRef, onChanged, onToast }: { cardRef: number; onChanged: () => void; onToast: (m: string) => void }) {
  const { notes, error, add, remove } = useNyNotes(cardRef, onChanged);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setErr(null);
    try { await add(t); setText(""); } catch (e) { setErr(errText(e, "Не удалось сохранить комментарий")); } finally { setBusy(false); }
  };
  const del = async (id: number) => {
    try { await remove(id); onToast("Комментарий удалён"); } catch (e) { onToast(errText(e, "Не удалось удалить комментарий")); }
  };

  return (
    <div className={ny.panel}>
      <div className={ny.add}>
        <AutoGrowTextarea className={ny.input} value={text} onChange={(e) => { setText(e.target.value); setErr(null); }} maxLength={MAX} minRows={2} maxHeight={220}
          placeholder="Комментарий: о чём договорились, что обещали…" aria-label="Новый комментарий" />
        <button type="button" className={ny.btn} onClick={() => { void save(); }} disabled={busy || !text.trim()}>{busy ? "…" : "Добавить"}</button>
      </div>
      {err ? <div className={ny.err} role="alert">{err}</div> : null}
      {error && !notes ? <div className={ny.err}>Ошибка загрузки: {error}</div>
        : !notes ? <div className={ny.muted}>Загрузка…</div>
        : notes.length === 0 ? <div className={ny.muted}>Комментариев пока нет — первый появится здесь.</div>
        : notes.map((n) => (
          <div key={n.id} className={ny.note}>
            <div className={ny.noteHead}>
              <b>{n.author_name || "—"}</b><span>{fmtStamp(n.created_at)}</span>
              {n.can_delete ? <NyDel onDelete={() => del(n.id)} label="Удалить комментарий" /> : null}
            </div>
            <div className={ny.noteText}>{n.text}</div>
          </div>
        ))}
    </div>
  );
}
